<template>
  <div class="importErrors">
    <div v-if="isLoadingSave">
      <span
        ><v-progress-circular
          indeterminate
          :size="20"
          color="blue lighten-5"
          class="mr-2"
        ></v-progress-circular
        >Saving...</span
      >
    </div>

    <div v-else class="importErrorTable">
      <div v-if="errorList.length == 0 && !isLoadingValidation">
        <v-alert type="success" outlined dense>No errors found</v-alert>
      </div>
      <v-data-table
        v-if="(errorList && errorList.length) || isLoadingValidation"
        v-model="selectedErrors"
        :headers="headers"
        :items="errorList"
        :items-per-page="15"
        item-key="Error"
        class="mb-8"
        :loading="isLoadingValidation"
        loading-text="Loading validation errors"
        dense
      >
        <template v-slot:[`item.dismiss`]="{ item }">
          <v-icon color="error" @click="deleteImportError(item)"> mdi-close-circle-outline </v-icon>
        </template>
        <template v-slot:[`item.viewDetails`]="{ item }">
          <v-btn text color="primary" @click="getErrorDetails(item)">View details</v-btn>
        </template>
        <template v-slot:[`item.exportDetails`]="{ item }">
          <v-btn text color="primary" @click="exportErrorDetails(item)">Export details</v-btn>
        </template>
      </v-data-table>

      <div class="mb-8">
        <v-data-table
          v-if="errorDetails.length"
          :headers="errorDetailsHeaders"
          :items="errorDetails"
          item-key="RowId"
          :items-per-page="-1"
          dense
        ></v-data-table>
      </div>

      <div v-if="isImportSuccessful">
        <v-alert type="success" outlined dense>Import is saved successfully</v-alert>
        <v-menu v-if="this.importId > 0" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn :loading="loading" v-bind="attrs" v-on="on">Print Results</v-btn>
          </template>
          <v-list>
            <v-list-item @click="exportImportResults('xlsx')">
              <v-list-item-title>Export Excel</v-list-item-title>
            </v-list-item>
            <v-list-item @click="exportImportResults('pdf')">
              <v-list-item-title>Export PDF</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImportErrors',
  components: {},
  props: {
    errorList: Array,
    importKey: { type: String, require: true },
    importId: Number,
    isLoadingValidation: Boolean,
    isLoadingSave: Boolean,
    isImportSuccessful: Boolean,
  },

  data() {
    return {
      loading: false,
      headers: [
        { text: 'Dismiss', value: 'dismiss', align: 'center', width: 76, sortable: false },
        { text: 'Error', value: 'Error', width: 80 },
        { text: 'Message', value: 'Msg' },
        { text: 'Count', value: 'Count', width: 88 },
        {
          text: 'View Details',
          value: 'viewDetails',
          width: 120,
          align: 'center',
          sortable: false,
        },
        {
          text: 'Export Details',
          value: 'exportDetails',
          width: 120,
          align: 'center',
          sortable: false,
        },
      ],
      errorDetails: [],
      selectedErrors: [],
    };
  },

  computed: {
    errorDetailsHeaders() {
      const result = [];
      for (let prop in this.errorDetails[0]) {
        result.push({
          text: prop,
          value: prop,
        });
      }
      return result;
    },
  },

  methods: {
    async getErrorDetails(item) {
      this.selectedErrors = [item];
      try {
        const response = await this.$api.get(
          `/Import/GetErrorDetails?importKey=${this.importKey}&errorCode=${item.Error}`
        );
        if (!response.data) {
          const msg = 'Unable to get error details';
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }
        this.errorDetails = response.data;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },

    async exportImportResults(format) {
      try {
        this.loading = true;
        let params = {};

        params['ReportName'] = "PayrollImport";

        params['InvestRunId'] = this.importId;

        const isPdf = format === 'pdf';
        const exportEndpoint = isPdf
          ? '/ReportService/ExportReportToPdf'
          : '/ReportService/ExportReportToExcel';
        const mimeType = isPdf
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const fileExtension = isPdf ? 'pdf' : 'xlsx';

        const response = await this.$api.post(exportEndpoint, params, {
          responseType: 'blob',
        });

        // Create a link to download the file
        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type: mimeType,
          })
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `PayrollImportResults.${fileExtension}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.loading = false;
      } catch (err) {
        this.loading = false;
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    async exportErrorDetails(item) {
      this.selectedErrors = [item];
      try {
        const response = await this.$api.get(
          `/Import/ExportErrorDetails?importKey=${this.importKey}&errorCode=${item.Error}`
        );
        if (!response.data) {
          const msg = 'Unable to get export details';
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }

        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type: 'application/csv',
          })
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'ImportErrorResults.csv');
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },

    async deleteImportError(item) {
      try {
        const response = await this.$api.delete(
          `/Import/DeleteImportErrors?importKey=${this.importKey}&errorCode=${item.Error}`
        );

        if (response.data) {
          const index = this.errorList.indexOf(item);
          this.errorList.splice(index, 1);
          this.errorDetails = [];
        } else {
          this.$store.commit('setNotificationMessage', {
            type: 'error',
            text: "You can't dismiss this error",
          });
        }
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.importErrors {
  max-width: 1200px;

  .v-data-table tbody tr {
    cursor: default;
  }
}
</style>
